import { Folder, FolderOpen, Terminal, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useState } from 'react';

interface CliCommand {
  displayName: string;
  description: string;
  command_name: string;
}

interface CliSubgroup {
  displayName: string;
  description?: string;
  commands?: Record<string, CliCommand>;
  subgroups?: Record<string, CliSubgroup>;
}

interface CliCommandGroup {
  displayName: string;
  description: string;
  commands?: Record<string, CliCommand>;
  subgroups?: Record<string, CliSubgroup>;
}

interface CliSchema {
  version: string;
  lastUpdated: string;
  cli_command_groups: Record<string, CliCommandGroup>;
}

interface CliPermissionsTreeProps {
  schema: CliSchema;
  permissions: Record<string, any>;
  onChange: (permissions: Record<string, any>) => void;
}

export function CliPermissionsTree({
  schema,
  permissions,
  onChange,
}: CliPermissionsTreeProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const handleGroupAllChange = (groupKey: string, checked: boolean) => {
    const newPermissions = { ...permissions };

    if (!newPermissions[groupKey]) {
      newPermissions[groupKey] = {};
    }

    newPermissions[groupKey].all = checked;

    onChange(newPermissions);
  };

  const handleSubgroupAllChange = (groupKey: string, path: string[], checked: boolean) => {
    const newPermissions = { ...permissions };

    // Navigate to the subgroup location
    if (!newPermissions[groupKey]) {
      newPermissions[groupKey] = {};
    }

    let current = newPermissions[groupKey];
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }

    const subgroupKey = path[path.length - 1];
    if (!current[subgroupKey]) {
      current[subgroupKey] = {};
    }
    current[subgroupKey].all = checked;

    onChange(newPermissions);
  };

  const handleCommandChange = (groupKey: string, path: string[], commandKey: string, checked: boolean) => {
    const newPermissions = { ...permissions };

    if (!newPermissions[groupKey]) {
      newPermissions[groupKey] = {};
    }

    // Navigate to the command location
    let current = newPermissions[groupKey];
    for (const segment of path) {
      if (!current[segment]) {
        current[segment] = {};
      }
      current = current[segment];
    }

    current[commandKey] = checked;

    onChange(newPermissions);
  };

  const isGroupAllChecked = (groupKey: string): boolean => {
    return permissions[groupKey]?.all === true;
  };

  const isSubgroupAllChecked = (groupKey: string, path: string[]): boolean => {
    if (!permissions[groupKey]) return false;

    let current = permissions[groupKey];
    for (const segment of path) {
      if (!current[segment]) return false;
      current = current[segment];
    }

    return current.all === true;
  };

  const isCommandChecked = (groupKey: string, path: string[], commandKey: string): boolean => {
    // Check group.all first
    if (permissions[groupKey]?.all === true) return true;

    // Navigate through path checking for any "all" flags
    if (!permissions[groupKey]) return false;

    let current = permissions[groupKey];
    for (const segment of path) {
      if (current.all === true) return true; // Parent has "all"
      if (!current[segment]) return false;
      current = current[segment];
    }

    // Check specific command
    return current[commandKey] === true;
  };

  const isAnyParentAll = (groupKey: string, path: string[]): boolean => {
    if (permissions[groupKey]?.all === true) return true;

    let current = permissions[groupKey];
    if (!current) return false;

    for (const segment of path) {
      if (current.all === true) return true;
      if (!current[segment]) return false;
      current = current[segment];
    }

    return false;
  };

  // Recursive component for rendering subgroups
  const renderSubgroup = (
    groupKey: string,
    subgroupKey: string,
    subgroup: CliSubgroup,
    parentPath: string[],
    level: number = 1
  ) => {
    const currentPath = [...parentPath, subgroupKey];
    const isExpanded = expandedGroups.has(`${groupKey}.${currentPath.join('.')}`);
    const isAllChecked = isSubgroupAllChecked(groupKey, currentPath);
    const isParentAll = isAnyParentAll(groupKey, parentPath);

    return (
      <div key={subgroupKey} className={cn('border-l-2 border-gray-200 dark:border-gray-700', level > 1 && 'ml-4')}>
        <div className="pl-4 py-2">
          {/* Subgroup header with "All" checkbox */}
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => toggleGroup(`${groupKey}.${currentPath.join('.')}`)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
            >
              <ChevronDown
                className={cn(
                  'w-4 h-4 transition-transform',
                  !isExpanded && '-rotate-90'
                )}
              />
            </button>
            <FolderOpen className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-sm">{subgroup.displayName}</span>
          </div>

          {isExpanded && (
            <div className="ml-6 space-y-2">
              {/* Subgroup "All" checkbox */}
              <div className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={isAllChecked}
                  onChange={(e) =>
                    handleSubgroupAllChange(groupKey, currentPath, e.target.checked)
                  }
                  disabled={isParentAll}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
                />
                <label className="text-sm font-medium cursor-pointer">
                  All {subgroup.displayName} commands
                </label>
              </div>

              {/* Direct commands under subgroup */}
              {subgroup.commands &&
                Object.entries(subgroup.commands).map(([cmdKey, cmd]) => (
                  <div key={cmdKey} className="flex items-start gap-2 py-1 ml-6">
                    <input
                      type="checkbox"
                      checked={isCommandChecked(groupKey, currentPath, cmdKey)}
                      onChange={(e) =>
                        handleCommandChange(groupKey, currentPath, cmdKey, e.target.checked)
                      }
                      disabled={isParentAll || isAllChecked}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50 mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Terminal className="w-3 h-3 text-gray-400" />
                        <label className="text-sm cursor-pointer">{cmd.displayName}</label>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 ml-5">
                        {cmd.description}
                      </p>
                      <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded ml-5">
                        {cmd.command_name}
                      </code>
                    </div>
                  </div>
                ))}

              {/* Nested subgroups (recursive) */}
              {subgroup.subgroups &&
                Object.entries(subgroup.subgroups).map(([nestedKey, nestedSubgroup]) =>
                  renderSubgroup(groupKey, nestedKey, nestedSubgroup, currentPath, level + 1)
                )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {Object.entries(schema.cli_command_groups || {}).map(([groupKey, group]) => {
        const isExpanded = expandedGroups.has(groupKey);
        const isAllChecked = isGroupAllChecked(groupKey);

        return (
          <div
            key={groupKey}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            {/* Group header */}
            <div className="bg-gray-50 dark:bg-gray-800 p-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleGroup(groupKey)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                >
                  <ChevronDown
                    className={cn(
                      'w-5 h-5 transition-transform',
                      !isExpanded && '-rotate-90'
                    )}
                  />
                </button>
                <Folder className="w-5 h-5 text-blue-500" />
                <div className="flex-1">
                  <h3 className="font-semibold">{group.displayName}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{group.description}</p>
                </div>
                <code className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">
                  {groupKey}
                </code>
              </div>
            </div>

            {/* Group content */}
            {isExpanded && (
              <div className="p-4 space-y-3">
                {/* Group "All" checkbox */}
                <div className="flex items-center gap-2 py-2 bg-blue-50 dark:bg-blue-900/20 px-3 rounded">
                  <input
                    type="checkbox"
                    checked={isAllChecked}
                    onChange={(e) =>
                      handleGroupAllChange(groupKey, e.target.checked)
                    }
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label className="text-sm font-semibold cursor-pointer">
                    All {group.displayName} commands
                  </label>
                </div>

                {/* Direct commands under group */}
                {group.commands && (
                  <div className="space-y-2 ml-6">
                    {Object.entries(group.commands).map(([cmdKey, cmd]) => (
                      <div key={cmdKey} className="flex items-start gap-2 py-1">
                        <input
                          type="checkbox"
                          checked={isCommandChecked(groupKey, [], cmdKey)}
                          onChange={(e) =>
                            handleCommandChange(groupKey, [], cmdKey, e.target.checked)
                          }
                          disabled={isAllChecked}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50 mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Terminal className="w-4 h-4 text-gray-400" />
                            <label className="text-sm cursor-pointer">{cmd.displayName}</label>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                            {cmd.description}
                          </p>
                          <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded ml-6">
                            {cmd.command_name}
                          </code>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Subgroups */}
                {group.subgroups && (
                  <div className="space-y-2">
                    {Object.entries(group.subgroups).map(([subgroupKey, subgroup]) =>
                      renderSubgroup(groupKey, subgroupKey, subgroup, [], 1)
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
